import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from './Icons';
import { apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const FbrTokenBanner = () => {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  useEffect(() => {
    // Only check for tenant users (not admins)
    if (!user || user.role === 'admin') {
      setShowBanner(false);
      return;
    }

    checkFbrTokenStatus();
  }, [user]);

  const checkFbrTokenStatus = async () => {
    try {
      const response = await apiGet('/api/tenant/fbr-token');
      if (response.ok) {
        const data = response.data;
        
        // Combine missing fields with token status
        const allMissingFields = [...(data.missing_fields || [])];
        if (!data.has_token) {
          allMissingFields.unshift('fbr_token'); // Add token to the beginning
        }
        
        // Show banner if there are any missing fields
        setShowBanner(allMissingFields.length > 0);
        setMissingFields(allMissingFields);
      }
    } catch (error) {
      console.error('Error checking FBR token status:', error);
      // Don't show banner on error
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in sessionStorage (resets on browser close)
    sessionStorage.setItem('fbr_banner_dismissed', 'true');
  };

  // Check if banner was dismissed in this session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('fbr_banner_dismissed');
    if (wasDismissed === 'true') {
      setDismissed(true);
    }
  }, []);

  // Helper function to convert field names to user-friendly labels
  const getFieldLabel = (field) => {
    const labels = {
      'fbr_token': 'FBR Token',
      'tax_number': 'CNIC',
      'seller_ntn_cnic': 'NTN',
      'seller_business_name': 'Business Name',
      'seller_address': 'Address',
      'seller_province': 'Province'
    };
    return labels[field] || field;
  };

  if (!showBanner || dismissed) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                FBR Configuration Incomplete
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                {missingFields.length > 0 ? (
                  <>
                    Please configure the following to enable FBR invoice submission: <strong>{missingFields.map(getFieldLabel).join(', ')}</strong>.
                  </>
                ) : (
                  'FBR token is required to submit invoices to FBR.'
                )}
                <a href="/settings" className="font-medium underline ml-1">
                  Configure in Settings
                </a>
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 ml-4 text-yellow-600 hover:text-yellow-800"
            aria-label="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FbrTokenBanner;

